/**
 * Единственная точка HTTP-доступа к API. Компоненты не вызывают fetch напрямую.
 *
 * Бекенд отвечает конвертом {ok:true,data} | {ok:false,error} (rework/docs/CONVENTIONS.md).
 *
 * Авторизация — Bearer-токен (cookie больше не используются, см. api/tokens.ts).
 * Токен доступа живёт сутки. Когда он протухает, сервер отвечает 401, и клиент САМ,
 * не спрашивая пользователя, меняет refresh-токен на новую пару и повторяет запрос.
 * Пользователь этого не замечает. Если и refresh не принят (полгода прошло, сессию
 * отозвали, токен утёк и цепочку погасили) — сессия стирается и мы уходим на страницу входа.
 *
 * Обновление идёт ОДНИМ запросом на все параллельные (см. refreshing ниже): refresh
 * одноразовый, и два одновременных обмена погасили бы сессию друг другу.
 */

import {
  clearTokens,
  getRefreshToken,
  getToken,
  kindForPath,
  saveTokens,
  type TokenKind,
  type TokenPair,
} from '@/api/tokens'
import { clearCache } from '@/lib/apiCache'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Не авторизован — вызывающий код обычно редиректит на /login. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }
}

type Envelope<T> = { ok: true; data: T } | { ok: false; error: string }

/** Один общий обмен на все запросы, застрявшие на 401 одновременно. */
const refreshing: Record<TokenKind, Promise<boolean> | null> = { user: null, admin: null }

/** Куда отправлять, когда сессия окончательно потеряна. */
function loginPath(kind: TokenKind): string {
  return kind === 'admin' ? '/admin/login' : '/login'
}

/**
 * Обменять refresh на новую пару. true — получилось.
 * Параллельные вызовы разделяют один и тот же запрос.
 */
function refreshSession(kind: TokenKind): Promise<boolean> {
  const inFlight = refreshing[kind]
  if (inFlight !== null) return inFlight

  const attempt = (async (): Promise<boolean> => {
    const refreshToken = getRefreshToken(kind)
    if (refreshToken === null) return false

    const path = kind === 'admin' ? '/api/admin/auth/refresh' : '/api/auth/refresh'
    try {
      const response = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })
      const envelope = (await response.json()) as Envelope<TokenPair>
      if (!envelope.ok) return false

      saveTokens(kind, envelope.data)
      return true
    } catch {
      return false   // сети нет — это не «выкинуть на вход», просто не вышло
    }
  })()

  refreshing[kind] = attempt
  void attempt.finally(() => {
    refreshing[kind] = null
  })
  return attempt
}

/** Сессия потеряна окончательно: чистим и уводим на страницу входа. */
function dropSession(kind: TokenKind): void {
  clearTokens(kind)
  // Кеш страниц принадлежал этому участку/админу — следующий вошедший не должен
  // увидеть чужой баланс или чужие контакты, мелькнувшие из памяти вкладки.
  clearCache()
  const target = loginPath(kind)
  if (window.location.pathname !== target) window.location.assign(target)
}

/** Один сетевой заход (без логики обновления). */
async function send(method: string, path: string, body?: unknown): Promise<Response> {
  const headers: Record<string, string> = {}

  const kind = kindForPath(path)
  const token = kind === null ? null : getToken(kind)
  if (token !== null) headers['Authorization'] = `Bearer ${token}`

  let payload: BodyInit | undefined
  if (body instanceof FormData) {
    payload = body
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(body)
  }

  return fetch(path, { method, headers, body: payload })
}

/**
 * Запрос с автоматическим продлением сессии: 401 → обменять refresh → повторить один раз.
 * Повтор ровно один: если и после свежего токена снова 401, дело не в сроке жизни.
 */
async function fetchWithRefresh(method: string, path: string, body?: unknown): Promise<Response> {
  let response = await send(method, path, body)
  if (response.status !== 401) return response

  const kind = kindForPath(path)
  if (kind === null) return response          // мы и не были залогинены — отдаём 401 как есть

  const renewed = await refreshSession(kind)
  if (!renewed) {
    dropSession(kind)
    return response
  }

  response = await send(method, path, body)
  if (response.status === 401) dropSession(kind)
  return response
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetchWithRefresh(method, path, body)

  const text = await response.text()
  let envelope: Envelope<T>
  try {
    envelope = JSON.parse(text) as Envelope<T>
  } catch {
    throw new ApiError(`Некорректный ответ сервера (${response.status})`, response.status)
  }

  if (!envelope.ok) throw new ApiError(envelope.error, response.status)
  return envelope.data
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),

  /** Загрузка файлов (multipart): документы, слайды, выписка банка. */
  upload: <T>(path: string, form: FormData) => request<T>('POST', path, form),

  /**
   * Закрытая авторизацией картинка → blob-ссылка для <img src>.
   *
   * Браузер не шлёт заголовок Authorization, загружая <img>, — ровно та же беда, что со
   * ссылками на документы. Поэтому картинку качаем сами и отдаём ссылку на blob.
   * Освобождать её должен вызывающий (URL.revokeObjectURL), иначе память течёт —
   * этим занимается хук useAuthImage.
   */
  async blobUrl(path: string): Promise<string> {
    const response = await fetchWithRefresh('GET', path)
    if (!response.ok) throw new ApiError(`Не удалось загрузить изображение (${response.status})`, response.status)
    return URL.createObjectURL(await response.blob())
  },

  /**
   * Скачать закрытый авторизацией файл (PDF-квитанция, документ) и отдать браузеру.
   *
   * Обычная ссылка <a href> здесь не годится: браузер не пошлёт по ней заголовок
   * Authorization, а токен в адресе — это токен в истории, логах прокси и «Поделиться».
   * Поэтому качаем сами (с заголовком и с продлением сессии), а файл отдаём через blob.
   *
   * inline=true — открыть в соседней вкладке (так смотрят PDF-квитанцию),
   * иначе браузер предложит сохранить.
   *
   * body задан — уходит POST (в legacy это была нативная POST-форма с target="_blank";
   * форма тоже не носит заголовок Authorization, поэтому и она заменена на fetch).
   */
  async downloadFile(path: string, filename: string, inline = false, body?: unknown): Promise<void> {
    const response = await fetchWithRefresh(body === undefined ? 'GET' : 'POST', path, body)
    if (!response.ok) {
      const text = await response.text()
      let message = `Не удалось скачать файл (${response.status})`
      try {
        const envelope = JSON.parse(text) as Envelope<unknown>
        if (!envelope.ok) message = envelope.error
      } catch {
        /* сервер ответил не JSON — оставляем общий текст */
      }
      throw new ApiError(message, response.status)
    }

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    try {
      if (inline) {
        window.open(url, '_blank', 'noopener')
      } else {
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        link.remove()
      }
    } finally {
      // вкладке с PDF нужно время открыться, иначе ссылка на blob умрёт у неё под руками
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    }
  },
}
